import React from "react";
import {
    FLOATING_LABEL_CLASS_NAME,
    FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
    FLOATING_LABEL_USING_VALID_CLASS_NAME,
    FORM_CLASS_NAME,
    FORM_ERROR_CLASS_NAME,
    FORM_ERROR_LIST_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_FOCUSED_CLASS_NAME,
    FORM_FIELD_HAS_ERRORS_CLASS_NAME,
    FORM_FIELD_NOT_BLANK_CLASS_NAME,
    FORM_FIELD_RADIO_CHOICE_STYLE,
    FORM_LABEL_CLASS_NAME,
    FORM_LABEL_DYNAMIC_CLASS_NAME,
    FORM_LABEL_OUTSIDE_CLASS_NAME,
    FORM_RADIO_OPTIONS_CLASS_NAME,
    FORM_RADIO_OPTION_CLASS_NAME,
    FORM_RADIO_OPTION_LABEL_CHECKED_CLASS_NAME,
    FORM_RADIO_OPTION_LABEL_CLASS_NAME,
    PSEUDO_PLACEHOLDER_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { UnstyledSelect } from "../UnstyledSelect";

import styles from "./index.module.scss";

// Native render of the legacy `form-states.html` element fixture: every BEM
// .form state the styleguide demonstrates, applying the same module+literal
// class pairs the real form components emit so the fixture keeps rendering
// identically when the legacy partials are torn down (#34958). Markup mirrors
// the old static fixture byte-for-byte where it matters for pixels; the
// explicit {" "} text nodes reproduce the whitespace the fragment prettifier
// put between inline siblings, which the baselines render as visible gaps.
// Classes with no module home stay literal on purpose: dynamic
// `form__field--<name>` hooks, the legacy `.form-container`/`.field` section,
// and `.select--large-down-arrow` (T2.7's).

const DYNAMIC_LABEL_CLASSES = concatClassNames([
    FORM_LABEL_CLASS_NAME,
    FORM_LABEL_DYNAMIC_CLASS_NAME,
]);

const RADIO_OPTION_LABEL_CHECKED_CLASSES = concatClassNames([
    FORM_RADIO_OPTION_LABEL_CLASS_NAME,
    FORM_RADIO_OPTION_LABEL_CHECKED_CLASS_NAME,
]);

export const StyleguideFormStates = (): React.JSX.Element => {
    return (
        <div
            id="element-tester"
            className="l-capped-width"
            style={{ padding: "2rem" }}
        >
            <h3>BEM .form block</h3>{" "}
            <div className={FORM_CLASS_NAME}>
                <div className={FORM_FIELD_CLASS_NAME}>
                    <label
                        className={DYNAMIC_LABEL_CLASSES}
                        htmlFor="f-dynamic"
                    >
                        Dynamic Label
                    </label>{" "}
                    <input id="f-dynamic" type="text" />
                </div>{" "}
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        FORM_FIELD_FOCUSED_CLASS_NAME,
                    ])}
                >
                    <label
                        className={DYNAMIC_LABEL_CLASSES}
                        htmlFor="f-focused"
                    >
                        Focused
                    </label>{" "}
                    <input id="f-focused" type="text" />
                </div>{" "}
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        FORM_FIELD_NOT_BLANK_CLASS_NAME,
                    ])}
                >
                    <label
                        className={DYNAMIC_LABEL_CLASSES}
                        htmlFor="f-not-blank"
                    >
                        Not Blank
                    </label>{" "}
                    <input
                        id="f-not-blank"
                        type="text"
                        defaultValue="Some value"
                    />
                </div>{" "}
                <div className={FORM_FIELD_CLASS_NAME}>
                    <label
                        className={concatClassNames([
                            FORM_LABEL_CLASS_NAME,
                            FORM_LABEL_OUTSIDE_CLASS_NAME,
                        ])}
                        htmlFor="f-outside"
                    >
                        Outside Label
                    </label>{" "}
                    <input id="f-outside" type="text" />
                </div>
            </div>{" "}
            <h3>Field-specific modifiers</h3>{" "}
            <div className={FORM_CLASS_NAME}>
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        "form__field--shipping_method_0",
                    ])}
                >
                    <label
                        className={FORM_LABEL_CLASS_NAME}
                        htmlFor="f-shipping"
                    >
                        Shipping Method
                    </label>{" "}
                    <input id="f-shipping" type="text" />
                </div>{" "}
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        "form__field--billing_addr_is_shipping_addr",
                    ])}
                >
                    <label>
                        <input type="checkbox" /> Same as shipping
                    </label>
                </div>{" "}
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        FORM_FIELD_RADIO_CHOICE_STYLE,
                        "form__field--radio-choice",
                    ])}
                >
                    <label>
                        <input type="radio" name="choice" /> Option A
                    </label>
                </div>{" "}
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        FORM_FIELD_HAS_ERRORS_CLASS_NAME,
                    ])}
                >
                    <label className={DYNAMIC_LABEL_CLASSES} htmlFor="f-error">
                        With Error
                    </label>{" "}
                    <input id="f-error" type="text" />{" "}
                    <ul className={FORM_ERROR_LIST_CLASS_NAME}>
                        <li className={FORM_ERROR_CLASS_NAME}>
                            This field is required.
                        </li>
                    </ul>
                </div>
            </div>{" "}
            <h3>Radio options</h3>{" "}
            <div className={FORM_FIELD_CLASS_NAME}>
                <fieldset className={FORM_RADIO_OPTIONS_CLASS_NAME}>
                    <legend className="ada-screenreader-only">Unchecked</legend>{" "}
                    <div className="form__radio-options__label">
                        <input type="radio" name="radio-opts" />{" "}
                        <span>Unchecked</span>
                    </div>
                </fieldset>{" "}
                <fieldset className={FORM_RADIO_OPTIONS_CLASS_NAME}>
                    <legend className="ada-screenreader-only">Third</legend>{" "}
                    <div className="form__radio-options__label">
                        <input type="radio" name="radio-opts" />{" "}
                        <span>Third</span>
                    </div>
                </fieldset>{" "}
                <fieldset className={FORM_RADIO_OPTIONS_CLASS_NAME}>
                    <legend className="ada-screenreader-only">Fourth</legend>{" "}
                    <div className="form__radio-options__label">
                        <input type="radio" name="radio-opts" />{" "}
                        <span>Fourth</span>
                    </div>
                </fieldset>
            </div>{" "}
            <h3>Radio option with checked state</h3>{" "}
            <div className={FORM_RADIO_OPTION_CLASS_NAME}>
                <label className={RADIO_OPTION_LABEL_CHECKED_CLASSES}>
                    <input type="radio" name="radio-opt" defaultChecked />{" "}
                    <span>Selected</span>
                </label>
            </div>{" "}
            <div className={FORM_RADIO_OPTION_CLASS_NAME}>
                <label className={FORM_RADIO_OPTION_LABEL_CLASS_NAME}>
                    <input type="radio" name="radio-opt" />{" "}
                    <span>Unselected</span>
                </label>
            </div>{" "}
            <h3>Financing plan radios</h3>{" "}
            <div
                className={concatClassNames([
                    FORM_FIELD_CLASS_NAME,
                    "form__field--financing_plan",
                ])}
            >
                <div className={FORM_RADIO_OPTION_CLASS_NAME}>
                    <label className={FORM_RADIO_OPTION_LABEL_CLASS_NAME}>
                        <input type="radio" name="plan" />{" "}
                        <span>60 months</span>
                    </label>
                </div>{" "}
                <div className={FORM_RADIO_OPTION_CLASS_NAME}>
                    <label className={RADIO_OPTION_LABEL_CHECKED_CLASSES}>
                        <input type="radio" name="plan" defaultChecked />{" "}
                        <span>36 months</span>
                    </label>
                </div>
            </div>{" "}
            <div className="form-options__financing-terms--choices">
                <div className={FORM_RADIO_OPTION_CLASS_NAME}>
                    <label>
                        <input type="radio" /> <span>Only option</span>
                    </label>
                </div>
            </div>{" "}
            <h3>Legacy .field with .form-container</h3>{" "}
            <div className="form-container">
                <div className="field text">
                    <label htmlFor="f-text">Text Label</label>{" "}
                    <div className="input-container">
                        <input id="f-text" type="text" />
                    </div>
                </div>{" "}
                <div className="field textarea">
                    <label htmlFor="f-textarea">Textarea Label</label>{" "}
                    <textarea id="f-textarea"></textarea>
                </div>{" "}
                <div className="field email">
                    <label htmlFor="f-email">Email Label</label>{" "}
                    <input id="f-email" type="email" />
                </div>{" "}
                <div className="field password">
                    <label htmlFor="f-password">Password Label</label>{" "}
                    <input id="f-password" type="password" />
                </div>{" "}
                <div className="field select">
                    <label htmlFor="f-select">Select Label</label>{" "}
                    <select id="f-select" className="state">
                        <option>Choose</option>
                        <option>NY</option>
                    </select>
                </div>{" "}
                <div className="field radio">
                    <label>
                        <input type="radio" /> Radio Label
                    </label>
                </div>{" "}
                <div className="field text">
                    <label className="mode-label" htmlFor="f-floated">
                        Floated Label
                    </label>{" "}
                    <input
                        id="f-floated"
                        type="text"
                        defaultValue="with value"
                    />
                </div>{" "}
                <div className="field text">
                    <label
                        className="disable-placeholder-mode"
                        htmlFor="f-static"
                    >
                        Static Label
                    </label>{" "}
                    <input id="f-static" type="text" />
                </div>{" "}
                <div className="field">
                    <p className="title">Title</p>{" "}
                    <p className="descr">Description text</p>{" "}
                    <input type="text" />{" "}
                    <p className="error-message">Error message</p>
                </div>
            </div>{" "}
            <span className="error">This is an error message</span>{" "}
            <h3>Select modifiers</h3>{" "}
            <UnstyledSelect noBorder>
                <option>No border</option>
            </UnstyledSelect>{" "}
            <UnstyledSelect className={styles.selectAltoBorder}>
                <option>Alto border</option>
            </UnstyledSelect>{" "}
            <select className="select--large-down-arrow">
                <option>Large arrow</option>
            </select>{" "}
            <select className={styles.selectSmallDownArrow}>
                <option>Small arrow</option>
            </select>{" "}
            <h3>Floating label</h3>{" "}
            <div style={{ background: "#262626", padding: "2rem" }}>
                <div
                    className={concatClassNames([
                        FLOATING_LABEL_CLASS_NAME,
                        FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
                    ])}
                >
                    <input
                        type="email"
                        placeholder=" "
                        defaultValue="typed@example.com"
                    />{" "}
                    <span className={PSEUDO_PLACEHOLDER_CLASS_NAME}>
                        Email Address
                    </span>{" "}
                    <label>Email Address</label>
                </div>{" "}
                <div
                    className={concatClassNames([
                        FLOATING_LABEL_CLASS_NAME,
                        FLOATING_LABEL_USING_VALID_CLASS_NAME,
                    ])}
                >
                    <input
                        type="email"
                        placeholder=" "
                        required
                        defaultValue="test@test.com"
                    />{" "}
                    <span className={PSEUDO_PLACEHOLDER_CLASS_NAME}>
                        Email Address
                    </span>{" "}
                    <label>Email Address</label>
                </div>{" "}
                <div className={FLOATING_LABEL_CLASS_NAME}>
                    <input type="email" disabled placeholder=" " />{" "}
                    <span className={PSEUDO_PLACEHOLDER_CLASS_NAME}>
                        Disabled
                    </span>{" "}
                    <label>Disabled</label>
                </div>
            </div>
        </div>
    );
};
